import { Option } from 'api/types/_public';
import { CONFIG_OPTION_TOAST_ERROR } from 'common/toast';
import DropdownOption from 'components/Common/DropdownOption';
import Hashtag from 'components/Common/Hashtag';
import LabelWrapper from 'components/Common/LabelWrapper';
import TooltipCustom from 'components/Common/TooltipCustom';
import { TYPE_IMAGE_OPTIONS, TYPE_SOURCE_IMAGE_OPTIONS } from 'helpers/constans';
import IconDownload from 'pages/ImageRepository/Icons/IconDownload';
import IconFavorite from 'pages/ImageRepository/Icons/IconFavorite';
import IconPrivate from 'pages/ImageRepository/Icons/IconPrivate';
import IconPublic from 'pages/ImageRepository/Icons/IconPublic';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { toast } from 'react-toastify';
import { Col, Row, Spinner } from 'reactstrap';
import { getImageLibraryNormal } from 'store/thunks';

interface BoxImageSearchProps {
    news_id?: number | string,
    onSetImageInsert: (image: any) => void,
    resetCursorToStart?: () => void,
}

const BoxImageSearch: React.ForwardRefRenderFunction<HTMLInputElement, BoxImageSearchProps> = ({ news_id, onSetImageInsert, resetCursorToStart }, ref) => {
    const { t } = useTranslation();
    const refBoxViewGenerate: any = ref;
    const refBoxViewImages = useRef<any>([]);

    const SORT_OPTIONS_LANG: Option[] = [{ label: t('Latest'), value: 'created_at' }, { label: t('Most favorites'), value: 'total_favorites' }, { label: t('Most downloaded'), value: 'total_downloads' }];

    const TYPE_OPTIONS_LANG: Option[] = TYPE_SOURCE_IMAGE_OPTIONS?.filter((item) => !!item?.value)?.map((item: Option) => ({ label: t(item?.label), value: `${item?.value}` }));

    const [sortSearch, setSortSearch] = useState<Option | null>(SORT_OPTIONS_LANG[0]);
    const [typeSearch, setTypeSearch] = useState<Option | null>(null);
    const [tagSearch, setTagSearch] = useState<any[]>([]);

    const [params, setParams] = useState<any>({ page: 1, limit: 10, sort_by: sortSearch?.value, source: typeSearch?.value || '', tags: (tagSearch || [])?.map((_tag: any) => _tag?.text).join(',') });

    const [images, setImage] = useState<any>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [nextPage, setNextPage] = useState<boolean>(false);
    const [isItemLoading, setIsItemLoading] = useState<number | null>(null);

    const handleSearch = async (_params: any = {}) => {
        try {
            if (isLoading) {
                return;
            }
            setIsLoading((_prev) => true);
            let _clone_params: any = structuredClone({ ...params, ..._params });
            const res: any = await getImageLibraryNormal(_clone_params);
            if (res?.code === 200) {
                setImage((_prev: any) => (_params?.page === 1 ? [...res?.data?.list] : [..._prev, ...res?.data?.list]));
                setNextPage((_prev: any) => (res?.data?.list?.length === Number(params?.limit)));
            }
            setIsLoading((_prev) => false);
        } catch (error: any) {
            toast(`${error?.message || ''}`, CONFIG_OPTION_TOAST_ERROR);
            setIsLoading((_prev) => false);
            return error;
        }
    };

    const handleNextPage = () => {
        const _page = params?.page + 1;
        handleSearch({ page: _page });
        setParams((_prev: any) => ({ ..._prev, page: _page }));
    };

    const handleSetImageInsert = async (item: any, index: number) => {
        try {
            if (isItemLoading !== null) {
                return;
            }
            setIsItemLoading((_prev) => index);
            const _image = {
                ...item,
                image_url: item?.storage_url || '',
            };
            onSetImageInsert && onSetImageInsert(_image);
            setIsItemLoading((_prev) => null);
        } catch (error: any) {
            setIsItemLoading((_prev) => null);
            toast(`${t('The original image does not exist!')}`, CONFIG_OPTION_TOAST_ERROR);
            return error;
        }
    };


    const handleSearchClick = () => {
        const _params = {
            page: 1,
            sort_by: sortSearch?.value || '',
            source: typeSearch?.value || '',
            tags: (tagSearch || [])?.map((_tag: any) => _tag?.text).join(','),
        };
        setImage((_prev: any) => []);
        setParams((_prev: any) => ({ ..._prev, ..._params }));
        handleSearch(_params);
    }

    useEffect(() => {
        const _params = { page: 1, sort_by: SORT_OPTIONS_LANG[0]?.value, source: '', tags: '' };
        setImage((_prev: any) => []);
        setParams((_prev: any) => ({ ..._prev, ..._params }));
        setSortSearch((_prev: any) => SORT_OPTIONS_LANG[0]);
        setTypeSearch((_prev: any) => null);
        setTagSearch((_prev: any) => []);
        handleSearch(_params);
    }, [news_id]);

    useEffect(() => {
        const handleClickOutside = (event: any) => {
            const target = event.target as HTMLElement;

            // Check if the click is outside all elements in refBoxViewImages
            const clickedOutsideImages = refBoxViewImages.current.every(
                (ref: any) => ref && !ref.contains(target)
            );

            // Check if the click is outside the element in refBoxViewGenerate
            const clickedOutsideGenerate =
                refBoxViewGenerate.current && !refBoxViewGenerate.current.contains(target);

            // Check if the click is inside an element with the class 'tox-dialog' (TinyMCE modal)
            const clickedInsideEditorModal = !!document.querySelector('.tox-dialog')?.contains(target);

            // If the click is outside both refBoxViewImages and refBoxViewGenerate, and not inside the editor modal
            if (clickedOutsideImages && clickedOutsideGenerate && !clickedInsideEditorModal) {
                resetCursorToStart && resetCursorToStart();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [resetCursorToStart, refBoxViewGenerate, refBoxViewImages]);

    const splitTagLong = (_tag: string = '') => {
        if (_tag?.length >= 15) {
            return (String(_tag || '')?.slice(0, 3) + "..." + String(_tag || '')?.slice(-1))
        }
        return _tag;
    };

    return (
        <div className="color-scrollbar-os overflow-y-auto overflow-x-hidden h-100 scrollable-div-google-search" id="scrollableDiv">
            <InfiniteScroll
                dataLength={images?.length || 0}
                next={handleNextPage}
                scrollableTarget='scrollableDiv'
                hasMore={nextPage}
                loader={''} // loader={<LoadingListNotify />}
                scrollThreshold={'50%'}
            >
                <Row className="box-google-image align-items-start align-content-start">
                    <Col sm={12}>
                        <Row style={{ borderBottom: '1px solid #c7c7c769' }}>
                            <Col col={6} sm={6} className='mt-2'>
                                <LabelWrapper label={t('Sorting')} isShow={true}>
                                    <DropdownOption
                                        name="sort"
                                        dataList={SORT_OPTIONS_LANG || []}
                                        placeholder={`${t("Sorting")}...`}
                                        className={`dropdown-status-rounded z-hight`}
                                        classNamePrefix="name-prefix"
                                        initialValue={sortSearch}
                                        isHaveMoreInfo={true}
                                        onChangeSelect={(e: any) => {
                                            setSortSearch(e);
                                        }}
                                    />
                                </LabelWrapper>
                            </Col>
                            <Col col={6} sm={6} className='mt-2'>
                                <LabelWrapper label={t('Source')} isShow={true}>
                                    <DropdownOption
                                        name="type"
                                        dataList={TYPE_OPTIONS_LANG || []}
                                        placeholder={`${t("Source")}...`}
                                        className={`dropdown-status-rounded z-hight`}
                                        classNamePrefix="name-prefix"
                                        initialValue={typeSearch || {
                                            label: t('All'),
                                            value: ''
                                        }}
                                        isHasOptionAll={true}
                                        optionAll={{
                                            label: t('All'),
                                            value: ''
                                        }}
                                        // isHaveMoreInfo={true}
                                        onChangeSelect={(e: any) => {
                                            setTypeSearch(e);
                                        }}
                                    />
                                </LabelWrapper>
                            </Col>
                            <Col col={8} sm={9} className='mt-3'>
                                <div className="position-relative">
                                    <LabelWrapper label={t('Tags')} isShow={!!tagSearch} >
                                        <Hashtag
                                            name="tags"
                                            placeholder={`${t("Search by tags")}...`}
                                            suggestions={[]}
                                            initialValue={tagSearch || []}
                                            onChangeTag={(event: any) => {
                                                setTagSearch(event);
                                            }}
                                            idSelected="hashtags-CampaignFormMulti"
                                            maxHashtags={2000}
                                            maxHeight="150px"
                                        />
                                    </LabelWrapper>
                                </div>
                            </Col>
                            <Col col={4} sm={3} className='mt-3'>
                                <div className="text-center">
                                    <button className="btn-md btn btn-soft-primary w-100" disabled={isLoading} onClick={() => handleSearchClick()}>
                                        {t('Button Search')}
                                    </button>
                                </div>
                            </Col>
                        </Row>
                    </Col>
                    <Col sm={12} className="mt-3">
                        <div className="row gallery-wrapper">
                            {images?.map((item: any, index: number) => (
                                <Col sm={6} key={index}>
                                    <div className="my-masonry-grid_row ps-0 cursor-pointer">
                                        <div className="element-item project designing development">
                                            <div className="gallery-box card px-0 py-0 mb-3">
                                                <div ref={(el: any) => (refBoxViewImages.current[index] = el)} className="gallery-container d-flex justify-content-center align-items-center" style={{ height: '150px', textAlign: 'center', backgroundColor: '#eaeaea' }} onClick={() => handleSetImageInsert(item, index)}>
                                                    <img className="gallery-img img-fluid mx-auto" style={{ minHeight: '150px' }} src={item?.thumbnail_url || ''} alt={t('No Image')} />
                                                    {(isItemLoading === index) && <span className="position-absolute fs-24">
                                                        <Spinner size="sm" className="text-white fs-20 align-middle" style={{ width: '40px', height: '40px' }}></Spinner>
                                                    </span>}
                                                </div>
                                                <div style={{ position: 'absolute', top: '6px', right: '5px' }} className="d-flex justify-content-end align-items-center  w-100 gap-2">
                                                    <TooltipCustom
                                                        title={!!item?.is_my ? t('My Image') : t('Public Image')}
                                                        className="d-inline-block vertical-align-middle ms-1"
                                                    // style={{ transform: 'translateY(1px)' }}
                                                    >
                                                        <button type="button" className="btn btn-sm text-center align-center"
                                                            style={{
                                                                padding: '0',
                                                                cursor: 'default'
                                                            }}
                                                        >
                                                            {!!item?.is_my ?
                                                                <div style={{ transform: 'translate(0px, -3.8px)' }}>
                                                                    <IconPrivate width={24} height={24} />
                                                                </div> : <div style={{ transform: 'translate(0px, -3.8px)' }}>
                                                                    <IconPublic width={23} height={23} />
                                                                </div>}
                                                        </button>
                                                    </TooltipCustom>
                                                </div>
                                                <div className="box-content">
                                                    <div className="d-flex align-items-center">
                                                        <div className="badge-wrapper d-flex flex-grow-1 gap-1 text-muted py-1">
                                                            {(item?.tags || [])?.map((tag: string) => <span className="badge rounded-1 border border-secondary text-secondary fs-11 fw-normal py-1 px-1" key={tag}>#{splitTagLong(tag || '')}</span>)}
                                                        </div>
                                                        <div className="flex-shrink-0">
                                                            <div className="d-flex gap-2">
                                                                <TooltipCustom
                                                                    title={t('Total Favorites')}
                                                                    className="d-inline-block vertical-align-middle"
                                                                >
                                                                    <button type="button" className="btn btn-sm fs-13 btn-link text-body text-decoration-none px-0" style={{ verticalAlign: '-webkit-baseline-middle' }}>
                                                                        <IconFavorite width={16} height={16} is_active={item?.is_favorite} />{item?.total_favorites || 0}
                                                                    </button>
                                                                </TooltipCustom>
                                                                <TooltipCustom
                                                                    title={t('Total Downloads')}
                                                                    className="d-inline-block vertical-align-middle"
                                                                >
                                                                    <button type="button" className="btn btn-sm fs-13 btn-link text-body text-decoration-none px-0" style={{ verticalAlign: '-webkit-baseline-middle' }}>
                                                                        <IconDownload width={14} height={14} />{item?.total_downloads || 0}
                                                                    </button></TooltipCustom>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                            ))}
                        </div>
                        <div className="text-center my-2">
                            {isLoading ? <Spinner size="sm" className="text-primary fs-20 align-middle" style={{ width: '30px', height: '30px' }}></Spinner> : <button className="btn-sm btn btn-primary" disabled={isLoading || !nextPage} style={{ width: '80px', borderRadius: '15px' }} onClick={() => handleNextPage()}>
                                {nextPage ? t('More') : t('No data')}
                            </button>}
                        </div>
                    </Col>
                </Row>
            </InfiniteScroll>
        </div>
    );
};

export default React.memo(React.forwardRef(BoxImageSearch));
